import { Component, OnDestroy, OnInit, Optional } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';import { Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { ApplicationContextService } from 'src/app/core/services/application-context.service';
import { CompanyUser } from 'src/app/core/models/application-context.model';
import { DelegatedUser, User } from 'src/app/core/services/auth.service';
import { UserProfileService } from 'src/app/core/services/user-profile.service';

@Component({
  selector: 'app-delegations-modal',
  templateUrl: './delegations-modal.component.html',
  styleUrls: ['./delegations-modal.component.scss'],
})
export class DelegationsModalComponent implements OnInit, OnDestroy {
  /** Set when context has a logged-in user (used to show body). */
  userId?: string;
  private readonly destroy$ = new Subject<void>();

  delegationUserOptions: Array<{ id: string; label: string }> = [];
  selectedDelegationGranteeId: string | null = null;
  isAddingDelegation = false;
  /** While a DELETE is in flight, the delegation row id being removed. */
  deletingDelegationId: string | null = null;
  delegatedUsersList: DelegatedUser[] = [];

  constructor(
    private readonly _applicationContextService: ApplicationContextService,
    private readonly _userProfileService: UserProfileService,
    @Optional() public bsModalRef?: BsModalRef
  ) {}

  ngOnInit(): void {
    this._applicationContextService.context
      .pipe(takeUntil(this.destroy$))
      .subscribe((context) => {
        const user = context?.currentlyLoggedInUser;
        if (!user) {
          return;
        }
        this.userId = user.id;
        this.setDelegatedUsersFromUser(user as User);
        this.rebuildDelegationUserOptions(
          context?.allAvailableUsers ?? [],
          user.id
        );
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeModal(): void {
    this.bsModalRef?.hide();
  }

  private setDelegatedUsersFromUser(user: User): void {
    const list = user?.delegatedUsers ?? [];
    this.delegatedUsersList = [...list].sort(
      (a, b) =>
        new Date(b.createdAt || 0).getTime() -
        new Date(a.createdAt || 0).getTime()
    );
  }

  private rebuildDelegationUserOptions(users: CompanyUser[], currentUserId: string): void {
    const uid = (currentUserId || '').trim().toLowerCase();
    const delegatedIds = new Set(
      (this.delegatedUsersList || [])
        .map((d) => (d.id || '').trim().toLowerCase())
        .filter(Boolean)
    );
    const rows = (users || [])
      .filter(
        (u) =>
          u?.id &&
          (u.id || '').trim().toLowerCase() !== uid &&
          !delegatedIds.has((u.id || '').trim().toLowerCase())
      )
      .map((u) => {
        const label = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.id;
        return { id: u.id, label };
      })
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    this.delegationUserOptions = rows;
  }

  onAddDelegation(): void {
    const granteeUserId = (this.selectedDelegationGranteeId || '').trim();
    if (!granteeUserId || this.isAddingDelegation) {
      return;
    }
    if (granteeUserId === (this.userId || '').trim()) {
return;
    }

    this.isAddingDelegation = true;
    this._userProfileService
      .createDelegation({ granteeUserId })
      .pipe(finalize(() => (this.isAddingDelegation = false)))
      .subscribe({
        next: () => {
this.selectedDelegationGranteeId = null;
          this._applicationContextService
            .refreshLoggedInUser()
            .pipe(take(1))
            .subscribe({
              error: () => {
                /* list refreshes on next navigation; toast already shown */
              },
            });
        },
        error: (err) => {
          console.error('Error creating delegation', err);
},
      });
  }

  isDeletingDelegation(delegationId: string): boolean {
    return this.deletingDelegationId === delegationId;
  }

  onDeleteDelegation(delegate: DelegatedUser): void {
    const delegationId = (delegate.delegationId || '').trim();
    if (!delegationId || this.deletingDelegationId) {
      return;
    }

    this.deletingDelegationId = delegationId;
    this._userProfileService
      .deleteDelegation(delegationId)
      .pipe(finalize(() => (this.deletingDelegationId = null)))
      .subscribe({
        next: () => {
this._applicationContextService
            .refreshLoggedInUser()
            .pipe(take(1))
            .subscribe({
              error: () => {
                /* list refreshes on next navigation; toast already shown */
              },
            });
        },
        error: (err) => {
          console.error('Error deleting delegation', err);
},
      });
  }
}
